import * as THREE from 'three';
import { isModelMesh } from './picking';
import { UnionFind, VertexWelder, countEdge, edgeEnds, edgeKey } from './weld';

// Faces this close in angle around an edge lie back to back, as the faces of
// two touching parts do, so neither is the other's neighbour.
const COINCIDENT_RADIANS = 1e-6;
const FULL_TURN = 2 * Math.PI;
const REUSED = -1;
const NO_WING = -1;

export interface BodyMetrics {
  size: { x: number; y: number; z: number };
  surfaceArea: number;
  /** Null unless the surface is closed: an open shell encloses nothing. */
  volume: number | null;
}

type VertexIds = Map<THREE.Mesh, Uint32Array>;

/** One triangle's use of an edge: the edge runs `from` to `to`, and `apex` is its third corner. */
interface Wing {
  mesh: number;
  from: number;
  to: number;
  apex: number;
}

function forEachTriangle(
  geometry: THREE.BufferGeometry,
  visit: (a: number, b: number, c: number) => void,
): void {
  const { index } = geometry;
  const count = index ? index.count : geometry.getAttribute('position').count;
  for (let i = 0; i + 2 < count; i += 3) {
    if (index) visit(index.getX(i), index.getX(i + 1), index.getX(i + 2));
    else visit(i, i + 1, i + 2);
  }
}

/** A connected piece of the model: meshes whose faces share edges. */
export class Body {
  private readonly members: ReadonlySet<THREE.Mesh>;

  constructor(
    readonly meshes: readonly THREE.Mesh[],
    private readonly vertexIds: VertexIds,
  ) {
    this.members = new Set(meshes);
  }

  has(mesh: THREE.Object3D): boolean {
    return this.members.has(mesh as THREE.Mesh);
  }

  metrics(): BodyMetrics {
    const box = new THREE.Box3();
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    const ab = new THREE.Vector3();
    const ac = new THREE.Vector3();
    const uses = new Map<number, number>();
    let area = 0;
    let volume = 0;

    for (const mesh of this.meshes) {
      const position = mesh.geometry.getAttribute('position');
      const ids = this.vertexIds.get(mesh);
      if (!position || !ids) continue;
      forEachTriangle(mesh.geometry, (i, j, k) => {
        a.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
        b.fromBufferAttribute(position, j).applyMatrix4(mesh.matrixWorld);
        c.fromBufferAttribute(position, k).applyMatrix4(mesh.matrixWorld);
        box.expandByPoint(a).expandByPoint(b).expandByPoint(c);
        area += ab.subVectors(b, a).cross(ac.subVectors(c, a)).length() / 2;
        volume += a.dot(ab.crossVectors(b, c)) / 6;
        countEdge(uses, ids[i], ids[j]);
        countEdge(uses, ids[j], ids[k]);
        countEdge(uses, ids[k], ids[i]);
      });
    }

    const closed = uses.size > 0 && [...uses.values()].every((count) => count === 2);
    const size = box.isEmpty() ? new THREE.Vector3() : box.getSize(new THREE.Vector3());
    return {
      size: { x: size.x, y: size.y, z: size.z },
      surfaceArea: area,
      volume: closed ? Math.abs(volume) : null,
    };
  }
}

interface FaceAround {
  mesh: number;
  angle: number;
  /** The way round the edge, +1 or -1, that leads into the face's solid; 0 for a sliver. */
  turn: number;
}

function nextRound(face: FaceAround, around: readonly FaceAround[]): FaceAround | null {
  if (face.turn === 0) return null;
  let partner: FaceAround | null = null;
  let nearest = Infinity;
  for (const other of around) {
    if (other.turn !== -face.turn) continue;
    let gap = ((((other.angle - face.angle) * face.turn) % FULL_TURN) + FULL_TURN) % FULL_TURN;
    if (gap < COINCIDENT_RADIANS || FULL_TURN - gap < COINCIDENT_RADIANS) gap = FULL_TURN;
    if (gap < nearest) {
      nearest = gap;
      partner = other;
    }
  }
  return partner;
}

/**
 * Where more than two faces meet at an edge, as they do along the seam of two
 * touching parts, each face's neighbour is the next face round the edge on the
 * side its solid lies.
 */
function pairAroundEdge(key: number, wings: readonly Wing[], welder: VertexWelder, meshes: UnionFind): void {
  const at = (id: number) => welder.positionOf(id, new THREE.Vector3());
  const [u, v] = edgeEnds(key);
  const origin = at(u);
  const axis = new THREE.Vector3().subVectors(at(v), origin).normalize();
  const acrossTo = (apex: number) => {
    const across = new THREE.Vector3().subVectors(at(apex), origin);
    return across.addScaledVector(axis, -across.dot(axis)).normalize();
  };
  const reference = acrossTo(wings[0].apex);
  const side = new THREE.Vector3().crossVectors(axis, reference);
  const along = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const spin = new THREE.Vector3();

  const around = wings.map((wing): FaceAround => {
    const across = acrossTo(wing.apex);
    const angle = Math.atan2(across.dot(side), across.dot(reference));
    along.subVectors(at(wing.to), at(wing.from));
    normal.subVectors(at(wing.apex), at(wing.from)).crossVectors(along, normal);
    const turn = Math.sign(-spin.crossVectors(axis, across).dot(normal));
    return { mesh: wing.mesh, angle: (angle + FULL_TURN) % FULL_TURN, turn };
  });

  for (const face of around) {
    const partner = nextRound(face, around);
    if (partner) meshes.union(face.mesh, partner.mesh);
  }
}

function turnCorner(at: number, by: number): number {
  return at - (at % 3) + ((at + by) % 3);
}

/** Per edge of one mesh, the corner that starts its only use, or REUSED. */
function soleUses(corner: (at: number) => number, corners: number): Map<number, number> {
  const firstUse = new Map<number, number>();
  for (let at = 0; at < corners - (corners % 3); at++) {
    const from = corner(at);
    const to = corner(turnCorner(at, 1));
    if (from === to) continue;
    const key = edgeKey(from, to);
    firstUse.set(key, firstUse.get(key) === undefined ? at : REUSED);
  }
  return firstUse;
}

// Each mesh's boundary edge uses, four numbers apiece: the mesh, the edge's two
// ends in winding order and the triangle's third corner. Uses of one edge are
// chained through `next` from the latest in `head`.
class BoundaryEdges {
  private readonly wings: number[] = [];
  private readonly next: number[] = [];
  private readonly head = new Map<number, number>();

  addMesh(mesh: number, ids: Uint32Array, index: ArrayLike<number> | undefined): void {
    const corner = index ? (at: number) => ids[index[at]] : (at: number) => ids[at];
    soleUses(corner, index?.length ?? ids.length).forEach((at, key) => {
      if (at === REUSED) return;
      this.next.push(this.head.get(key) ?? NO_WING);
      this.head.set(key, this.next.length - 1);
      this.wings.push(mesh, corner(at), corner(turnCorner(at, 1)), corner(turnCorner(at, 2)));
    });
  }

  join(meshes: UnionFind, welder: VertexWelder): void {
    this.head.forEach((first, key) => {
      const second = this.next[first];
      if (second === NO_WING) return;
      if (this.next[second] === NO_WING) {
        meshes.union(this.wings[first * 4], this.wings[second * 4]);
        return;
      }
      const around: Wing[] = [];
      for (let wing = first; wing !== NO_WING; wing = this.next[wing]) around.push(this.wingAt(wing));
      pairAroundEdge(key, around, welder, meshes);
    });
  }

  private wingAt(wing: number): Wing {
    const [mesh, from, to, apex] = this.wings.slice(wing * 4, wing * 4 + 4);
    return { mesh, from, to, apex };
  }
}

function weldVertices(meshes: readonly THREE.Mesh[], welder: VertexWelder): VertexIds {
  const vertexIds: VertexIds = new Map();
  const point = new THREE.Vector3();
  for (const mesh of meshes) {
    const position = mesh.geometry.getAttribute('position');
    const ids = new Uint32Array(position.count);
    for (let i = 0; i < position.count; i++) {
      ids[i] = welder.idOf(point.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld));
    }
    vertexIds.set(mesh, ids);
  }
  return vertexIds;
}

/**
 * The model's separate bodies. Previews carry no part structure — OCCT writes
 * every solid of a compound as one mesh per face — so a body is recovered as
 * the faces that share edges.
 */
export function bodiesOf(model: THREE.Object3D): Body[] {
  model.updateWorldMatrix(true, true);
  const meshes: THREE.Mesh[] = [];
  model.traverse((child) => {
    if (isModelMesh(child)) meshes.push(child);
  });
  if (meshes.length === 0) return [];

  const welder = new VertexWelder();
  const vertexIds = weldVertices(meshes, welder);
  const boundaries = new BoundaryEdges();
  meshes.forEach((mesh, m) => boundaries.addMesh(m, vertexIds.get(mesh) as Uint32Array, mesh.geometry.index?.array));
  const joined = new UnionFind(meshes.length);
  boundaries.join(joined, welder);

  const groups = new Map<number, THREE.Mesh[]>();
  meshes.forEach((mesh, m) => {
    const root = joined.find(m);
    const group = groups.get(root);
    if (group) group.push(mesh);
    else groups.set(root, [mesh]);
  });
  return [...groups.values()].map((group) => new Body(group, vertexIds));
}
